import { Link, useNavigate } from "react-router-dom";
import { useAuth, homeFor } from "../context/AuthContext";
import { Button } from "./ui/button";
import { ClipboardList, Calendar, LayoutDashboard, ShieldCheck, LogOut, Leaf, MessageSquare, User, BarChart3, ArrowLeft, ArrowUp } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";

const LINKS = {
  client: [
    { to: "/app", label: "My Evaluations", icon: ClipboardList },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/profile", label: "Profile", icon: User },
  ],
  clinician: [
    { to: "/clinician", label: "Cases", icon: LayoutDashboard },
    { to: "/clinician/availability", label: "Availability", icon: Calendar },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/profile", label: "Profile", icon: User },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard },
    { to: "/admin/qa", label: "QA Review", icon: ShieldCheck },
    { to: "/admin/bi", label: "Reports", icon: BarChart3 },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/profile", label: "Profile", icon: User },
  ],
};

export function DashboardShell({ title, subtitle, actions, banner, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = LINKS[user?.role] || [];
  const [unread, setUnread] = useState(0);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    let alive = true;
    const poll = () => api.get("/messages/unread-count").then((r) => alive && setUnread(r.data.unread)).catch(() => {});
    poll();
    const t = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 200);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF8] noise-bg">
      <header className="sticky top-0 z-40 bg-[#FAFAF8]/90 backdrop-blur-xl border-b border-[#E5E5DF]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} data-testid="back-button"
              className="h-8 w-8 rounded-full border border-[#E5E5DF] flex items-center justify-center text-[#5C5C57] hover:text-[#1E3F20] hover:border-[#7E9B82] transition-colors"
              title="Back">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <Link to="/" className="flex items-center gap-2" data-testid="brand-home-link">
              <span className="h-8 w-8 rounded-full bg-[#1E3F20] flex items-center justify-center">
                <Leaf className="h-4 w-4 text-[#FAFAF8]" />
              </span>
              <span className="font-heading text-lg font-semibold tracking-tight text-[#1C1C19] hidden sm:inline">
                Psychological & Functional Assessment Center
              </span>
            </Link>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                <Button variant="ghost" className="rounded-full text-sm text-[#5C5C57] hover:text-[#1E3F20] hover:bg-[#E3DAC9]/50 transition-colors relative">
                  <l.icon className="h-4 w-4 mr-2" /> {l.label}
                  {l.label === "Messages" && unread > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-[#8C2A2A] text-white text-[10px] font-bold flex items-center justify-center" data-testid="nav-unread-badge">
                      {unread}
                    </span>
                  )}
                </Button>
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-[#1C1C19]" data-testid="nav-user-name">{user?.name}</p>
              <p className="text-xs text-[#5C5C57] capitalize" data-testid="nav-user-role">{user?.role}</p>
            </div>
            <Button variant="outline" size="sm" data-testid="logout-button" className="rounded-full border-[#E5E5DF]"
              onClick={async () => { await logout(); navigate("/"); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-10">
        {banner}
        {(title || actions) && (
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1C1C19]" data-testid="page-title">{title}</h1>
              {subtitle && <p className="text-[#5C5C57] mt-2 max-w-2xl">{subtitle}</p>}
            </div>
            {actions}
          </div>
        )}
        {children}
      </main>
      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="back-to-top"
          className="fixed bottom-6 right-6 z-50 h-11 w-11 rounded-full bg-[#1E3F20] text-white shadow-lg flex items-center justify-center hover:bg-[#2C5A2E] transition-colors"
          title="Back to top">
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
